"use client";

import {
  RUNTIME_OFFER_PLACEMENT_HI_NAVAMSA_CHART_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_NAVAMSA_CHART_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";

import { useRef, useState, type RefObject } from "react";
import type { KundaliRequest } from "@ishvaram/api-client";

import BirthDetailsForm from "@/components/BirthDetailsForm";
import { NAVAMSA_CARD_COPY } from "./_form-copy";
import { NAVAMSA_FORM_HEADING } from "./_form-heading";
import { OfferPlacement } from "@/components/OfferPlacement";
import { pageReportBridgeOffer } from "@/components/OfferPlacement.page-bridge";
import VargaChartDiagram from "@/components/varga/VargaChartDiagram";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { calculateNavamsa, type NavamsaRequest, type NavamsaResponse } from "@/lib/api/navamsa";
import D9PlacementTable from "./_components/D9PlacementTable";
import { ToolInputError, toolErrorMessage } from "@/lib/tool-error-message";

type Language = "en" | "hi";

// Card controls (submit, errors) come from the guarded Hinglish card copy
// (c-forms-10); the card heading (Devanagari on /hi/, owner 2026-10-08) and
// the D9 result and offer copy below stay page copy.
const COPY = {
  en: {
    ...NAVAMSA_CARD_COPY.en,
    heading: NAVAMSA_FORM_HEADING.en,
    offerTitle: "Read your D9 in the full marriage context",
    offerBody:
      "The free result gives your engine-computed D9 placements. The Marriage Report adds the 7th house and lord, D1–D9 synthesis, Mangal Dosh, dasha timing, spouse indicators, and practical next steps.",
    offerCta: "Get Marriage Report",
    notesTitle: "Traditional D9 placement notes",
    notesBoundary:
      "These are placement-level interpretations, not a marriage prediction or compatibility verdict. Timing and relationship judgement require the D1 chart, 7th house, dashas, doshas, and partner context.",
    eyebrow: "Navamsa D9",
    lagna: "Navamsa Lagna:",
    vargottama: (count: number) =>
      ` Vargottama planet${count === 1 ? "" : "s"} — same rashi in D1 and D9.`,
    chart: "Your D9 Chart",
    tableTitle: "D1 and D9 placements",
    headers: [
      "Planet",
      "D1 rashi, degree, nakshatra",
      "D9 Rashi",
      "D9 House",
      "D9 Dignity",
      "Status",
    ],
    lagnaRow: "Lagna (Ascendant)",
    vargottamaBadge: "Vargottama",
    pushkaraBadge: "Pushkara",
    flagsNote:
      "Vargottama: the same rashi in D1 and D9. Pushkara: the D1 degree falls in one of the two navamshas of its sign that the classical table counts as Pushkara (fire signs: 7th and 9th; earth: 3rd and 5th; air: 6th and 8th; water: 1st and 3rd). Both are placement flags, not a reading.",
    engineNote:
      "Generated by Ishvaram Jyotish engine · Sidereal zodiac (Lahiri ayanamsa) · Reference: BPHS",
  },
  hi: {
    ...NAVAMSA_CARD_COPY.hi,
    heading: NAVAMSA_FORM_HEADING.hi,
    // Report-offer copy stays in the Hinglish offer register (rule 21/22).
    offerTitle: "Apni D9 ko complete marriage context me samjhein",
    offerBody:
      "Free result engine-computed D9 placements deta hai. Marriage Report me 7th house aur lord, D1–D9 synthesis, Mangal Dosh, dasha timing, spouse indicators aur practical next steps milte hain.",
    offerCta: "Marriage Report paayein",
    notesTitle: "D9 स्थिति पर पारंपरिक टिप्पणियाँ",
    notesBoundary:
      "ये ग्रह-स्थिति स्तर की व्याख्याएँ हैं, विवाह की भविष्यवाणी या अनुकूलता का निर्णय नहीं। समय और संबंध के निर्णय के लिए D1 कुंडली, सप्तम भाव, दशाएँ, दोष और साथी का संदर्भ ज़रूरी है।",
    eyebrow: "नवांश D9",
    lagna: "नवांश लग्न:",
    vargottama: () => " वर्गोत्तम ग्रह — D1 और D9 में एक ही राशि।",
    chart: "आपका D9 चार्ट",
    tableTitle: "D1 और D9 में स्थिति",
    headers: ["ग्रह", "D1 राशि, अंश, नक्षत्र", "D9 राशि", "D9 भाव", "D9 अवस्था", "स्थिति"],
    lagnaRow: "लग्न",
    vargottamaBadge: "वर्गोत्तम",
    pushkaraBadge: "पुष्कर",
    flagsNote:
      "वर्गोत्तम: D1 और D9 में एक ही राशि। पुष्कर: D1 का अंश अपनी राशि के उन दो नवांशों में से एक में है जिन्हें शास्त्रीय तालिका पुष्कर नवांश मानती है (अग्नि राशियाँ: सातवाँ और नौवाँ; पृथ्वी राशियाँ: तीसरा और पाँचवाँ; वायु राशियाँ: छठा और आठवाँ; जल राशियाँ: पहला और तीसरा)। दोनों केवल स्थिति-संकेत हैं, फलादेश नहीं।",
    engineNote:
      "Ishvaram ज्योतिष इंजन द्वारा गणना · निरयन राशिचक्र (लाहिड़ी अयनांश) · संदर्भ: BPHS",
  },
} as const;

type Copy = (typeof COPY)[Language];

export default function NavamsaChartClient({ language = "en" }: { language?: Language }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<NavamsaResponse | null>(null);
  const [submittedBirth, setSubmittedBirth] = useState<NavamsaRequest | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const copy = COPY[language];

  async function handleSubmit(data: KundaliRequest) {
    setError(undefined);
    setLoading(true);
    setResult(null);
    try {
      const request = toNavamsaRequest(data, copy);
      const response = await calculateNavamsa(request);
      setSubmittedBirth(request);
      setResult(response);
      trackCalculatorUsed({
        calculator: "navamsa-chart",
        language,
        source_path: sourcePath(language),
        has_time: true,
      });
      setTimeout(
        () => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        100
      );
    } catch (submitError: unknown) {
      setError(toolErrorMessage(submitError, language, copy.errorFallback));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="mb-10">
      <BirthDetailsForm
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        heading={copy.heading}
        headingLevel="h2"
        submitLabel={copy.submit}
        requireBirthTime
        requireResolvedPlace
        collectName={false}
        language={language}
        analyticsToolName="navamsa-chart"
        analyticsPlacement="navamsa_birth_details"
        rememberBirthDetails
        showTrustStrip
      />
      {result && submittedBirth ? (
        <NavamsaResults
          result={result}
          request={submittedBirth}
          resultRef={resultRef}
          language={language}
        />
      ) : null}
    </section>
  );
}

function toNavamsaRequest(data: KundaliRequest, copy: Copy): NavamsaRequest {
  if (!data.birth_time) throw new ToolInputError(copy.timeRequired);
  if (data.latitude == null || data.longitude == null) {
    throw new ToolInputError(copy.placeRequired);
  }
  return {
    birth_date: data.birth_date,
    birth_time: data.birth_time,
    birth_place: data.birth_place,
    latitude: data.latitude,
    longitude: data.longitude,
    timezone: data.timezone,
  };
}

function NavamsaResults({
  result,
  request,
  resultRef,
  language,
}: {
  result: NavamsaResponse;
  request: NavamsaRequest;
  resultRef: RefObject<HTMLDivElement | null>;
  language: Language;
}) {
  const copy = COPY[language];
  return (
    <div
      ref={resultRef}
      className="mt-8 rounded-2xl border border-border-default bg-surface-raised p-6"
      data-design-owner="navamsa-result"
    >
      <NavamsaSummary result={result} copy={copy} language={language} />
      <VargaChartDiagram
        title={copy.chart}
        lagnaSign={result.navamsa_lagna}
        planets={result.d9_chart.map((planet) => ({
          planet_id: planet.planet_id,
          sign: planet.d9_rashi,
          house: planet.d9_house,
        }))}
        locale={language}
      />
      <D9PlacementTable result={result} copy={copy} language={language} />
      <OfferPlacement
        {...pageReportBridgeOffer({
          source: sourcePath(language),
          placement: offerPlacement(language),
          language: language,
          birthContext: {
            birthDate: request.birth_date,
            birthTime: request.birth_time,
            birthPlace: request.birth_place,
          },
        })}
        headline={copy.offerTitle}
        subhead={copy.offerBody}
        ctaLabel={copy.offerCta}
      />
      <div className="mt-8">
        <h4 className="mb-2 font-serif text-subtitle-small font-bold text-text-primary">
          {copy.notesTitle}
        </h4>
        <p className="mb-3 rounded-lg border border-warning/30 bg-warning/10 p-3 text-body-small leading-relaxed text-warning">
          {copy.notesBoundary}
        </p>
        <PredictionsList predictions={result.predictions} />
      </div>
      <p className="mt-6 text-caption text-text-muted">{copy.engineNote}</p>
    </div>
  );
}

function NavamsaSummary({
  result,
  copy,
  language,
}: {
  result: NavamsaResponse;
  copy: Copy;
  language: Language;
}) {
  const isHi = language === "hi";
  return (
    <div className="mb-4">
      <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-intent-brand">
        {copy.eyebrow} · {result.birth_place} · {result.birth_date}
      </p>
      <h3 className="mt-1 font-serif text-title font-bold text-text-primary">
        {copy.lagna}{" "}
        <span className="text-intent-brand">
          {isHi ? result.navamsa_lagna_display_hi : result.navamsa_lagna_display_en}
        </span>
        {isHi ? null : (
          <span className="ml-2 text-body-small text-text-muted">
            {result.navamsa_lagna_display_hi}
          </span>
        )}
      </h3>
      {result.vargottama_count > 0 ? (
        <p className="mt-1 text-body-small text-text-muted">
          <strong className="text-text-primary">{result.vargottama_count}</strong>
          {copy.vargottama(result.vargottama_count)}
        </p>
      ) : null}
    </div>
  );
}

function PredictionsList({ predictions }: { predictions: string[] }) {
  return (
    <div className="space-y-2">
      {predictions.map((prediction) => (
        <p
          key={prediction}
          className="rounded-xl border border-border-default bg-surface-raised p-3 text-body-small text-text-primary"
        >
          {prediction}
        </p>
      ))}
    </div>
  );
}

function sourcePath(language: Language) {
  return language === "hi" ? "/hi/navamsa-chart" : "/navamsa-chart";
}

function offerPlacement(language: Language) {
  return language === "hi"
    ? RUNTIME_OFFER_PLACEMENT_HI_NAVAMSA_CHART_REPORT_BRIDGE
    : RUNTIME_OFFER_PLACEMENT_NAVAMSA_CHART_REPORT_BRIDGE;
}
